import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2, XCircle, Search, RefreshCw } from "lucide-react";

export default function AdminEmailLog() {
  const [data, setData] = useState({ rows: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [sentOnly, setSentOnly] = useState(null); // null | true | false

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "150");
      if (sentOnly !== null) params.set("sent", String(sentOnly));
      if (filter.trim()) params.set("to", filter.trim());
      const { data } = await api.get(`/admin/emails/log?${params}`);
      setData(data);
    } catch {
      toast.error("Could not load email log");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [sentOnly]);

  const sent30 = data.counts?.sent_30d || 0;
  const monthlyLimit = data.counts?.resend_free_limit_month || 3000;
  const usagePct = Math.min(100, Math.round((sent30 / monthlyLimit) * 100));

  return (
    <div className="space-y-6" data-testid="admin-email-log-page">
      <div>
        <div className="hs-overline text-blue-700">RESEND · TRANSACTIONAL EMAIL LOG</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Alerts & Emails</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-3xl">
          Every alert, welcome email, password reset, digest, and CSA notification the platform sends is logged here.
          Watch the 30-day usage vs. Resend&rsquo;s plan quota to know when to upgrade.
        </p>
      </div>

      {/* Usage strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="email-log-usage">
        <div className="hs-card p-4">
          <div className="hs-overline text-[10px] text-slate-500">SENT — LAST 24H</div>
          <div className="font-display font-black text-3xl font-mono mt-1">{data.counts?.sent_24h ?? 0}</div>
          <div className="text-[10px] text-slate-500 mt-1">Free daily limit: {data.counts?.resend_free_limit_day ?? 100}</div>
        </div>
        <div className="hs-card p-4">
          <div className="hs-overline text-[10px] text-slate-500">SENT — LAST 30D</div>
          <div className="font-display font-black text-3xl font-mono mt-1 text-emerald-600">{sent30}</div>
          <div className="text-[10px] text-slate-500 mt-1">Free monthly cap: {monthlyLimit}</div>
        </div>
        <div className="hs-card p-4">
          <div className="hs-overline text-[10px] text-slate-500">FAILED — LAST 30D</div>
          <div className={`font-display font-black text-3xl font-mono mt-1 ${(data.counts?.failed_30d || 0) > 0 ? "text-rose-600" : ""}`}>
            {data.counts?.failed_30d ?? 0}
          </div>
        </div>
        <div className="hs-card p-4" data-testid="email-quota-bar">
          <div className="hs-overline text-[10px] text-slate-500">QUOTA USED (Free tier)</div>
          <div className={`font-display font-black text-3xl font-mono mt-1 ${
            usagePct >= 90 ? "text-rose-600" : usagePct >= 70 ? "text-amber-600" : "text-emerald-600"
          }`}>{usagePct}%</div>
          <div className="mt-2 h-2 bg-slate-100 overflow-hidden rounded">
            <div className={`h-full ${
              usagePct >= 90 ? "bg-rose-600" : usagePct >= 70 ? "bg-amber-500" : "bg-emerald-500"
            }`} style={{ width: `${usagePct}%` }}></div>
          </div>
        </div>
      </div>

      {usagePct >= 70 && (
        <div className="hs-card p-4 border-l-4 border-amber-500 bg-amber-50/60" data-testid="email-quota-warn">
          <div className="text-sm">
            <strong>You&rsquo;re at {usagePct}% of Resend&rsquo;s free monthly quota.</strong> Once you cross 3,000 emails/month,
            emails will start failing. Move to a paid Resend plan (<strong>$20/mo → 50,000 emails/month</strong>) at{" "}
            <a href="https://resend.com/pricing" target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
              resend.com/pricing
            </a>. No code changes needed — just upgrade your Resend account.
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="hs-card p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search size={14} className="text-slate-400" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Filter by recipient email…"
            className="hs-input"
            data-testid="email-log-filter-input"
          />
        </div>
        <div className="flex gap-1">
          {[
            { v: null, label: "All" },
            { v: true, label: "Delivered" },
            { v: false, label: "Failed" },
          ].map((b) => (
            <button
              key={b.label}
              onClick={() => setSentOnly(b.v)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded ${
                sentOnly === b.v ? "bg-blue-700 text-white" : "bg-slate-100 hover:bg-slate-200"
              }`}
              data-testid={`email-log-filter-${b.label.toLowerCase()}`}
            >
              {b.label}
            </button>
          ))}
        </div>
        <button onClick={load} className="hs-btn-secondary inline-flex items-center gap-2"
                data-testid="email-log-refresh">
          <RefreshCw size={12} /> REFRESH
        </button>
      </div>

      {/* Log table */}
      <div className="hs-card p-0 overflow-hidden" data-testid="email-log-table">
        <div className="max-h-[600px] overflow-y-auto">
          {loading ? (
            <div className="p-6 hs-overline">LOADING…</div>
          ) : (data.rows || []).length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-500">
              <Mail size={28} className="mx-auto text-slate-300" />
              <p className="mt-3">No emails logged yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-[10px] hs-overline text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-3">Status</th>
                  <th className="px-3">To</th>
                  <th className="px-3">Subject</th>
                  <th className="px-3">Sent At</th>
                  <th className="px-3">Provider ID / Error</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3">
                      {r.sent ? (
                        <CheckCircle2 size={16} className="text-emerald-600" />
                      ) : (
                        <XCircle size={16} className="text-rose-600" />
                      )}
                    </td>
                    <td className="px-3 font-mono text-xs">{r.to}</td>
                    <td className="px-3">{r.subject}</td>
                    <td className="px-3 font-mono text-[11px] text-slate-500">
                      {(r.created_at || "").replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="px-3 font-mono text-[10px] text-slate-500 truncate max-w-[200px]"
                        title={r.error || r.provider_id}>
                      {r.error ? <span className="text-rose-600">{r.error}</span> : (r.provider_id || "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <p className="text-[11px] text-slate-400">
        Alerts logged: welcome emails, password resets, filing expiry warnings, driver credential warnings,
        document approval / rejection, CSA snapshot updates, weekly compliance digests, and support-chat alerts.
      </p>
    </div>
  );
}
